// src/views/RepriceRadar.jsx
//
// ═══════════════════════════════════════════════════════════════
// RE-PRICE RADAR — the reason the scenario history exists
// ═══════════════════════════════════════════════════════════════
// The 10-year swap drives the expected rate, and the expected rate drives the
// PLF. So when the swap drops, every borrower priced at the old rate qualifies
// for MORE money on the same house without lifting a finger — and none of them
// know it. This board is that call list.
//
// It reads the `scenarios` table (see supabase/2026-08-20-scenarios-and-
// contacts.sql) TOGETHER WITH the priced history saved on every file, finds
// everyone whose newest run was priced when the swap was higher than today's,
// and estimates what the same deal looks like now. One row per person — their
// most recent run across both sources — because calling someone twice off two
// stale quotes is worse than not calling.
//
// The dollar figure is an ESTIMATE, clearly labelled as one. It exists to rank
// the list, not to be quoted. The real number comes from opening the call and
// re-running the calculator, which is exactly what the button does.

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { C, Ic } from '../theme';
import { Badge, Btn, CallBtn } from '../ui';
import { convertedLoanFor, fmtWhen, leadOwner, ownerOf, prospectFromLead, prospectFromLoan, repriceCandidatesFromLoans } from '../lib';
import { fetchScenarioPeople, mergeRepriceCandidates, scenariosReady } from '../scenarioStore';
import { STAFF } from '../supabase';
import { OwnerChip } from './Dashboard';

const $=function(n){return (n||n===0)?"$"+Math.round(n).toLocaleString():"—";};

// Rough PLF sensitivity to the expected rate. HUD's tables move roughly
// 5-6 principal-limit points per 1.00% of expected rate through the range
// these deals actually live in. We use the conservative end and cap the
// estimate, because an over-promise here turns into a bad phone call.
const PLF_POINTS_PER_POINT=5.0;
function estimateGain(s,swapNow){
  var drop=(Number(s.swap_rate)||0)-swapNow;
  if(drop<=0)return 0;
  var hv=Number(s.home_value)||0;
  if(!hv)return 0;
  var gain=hv*(Math.min(drop,2.0)*PLF_POINTS_PER_POINT/100);
  return Math.max(0,Math.round(gain));
}

// ownerView ("mine" | "other" | "all", relative to `staff`) is Pipeline's
// Whose-files toggle; onOwnerCounts reports {mine,other,all} back to it.
export function RepriceRadar({rateSheets,onStartCall,loans,mktgLeads,staff,ownerView,onOwnerCounts}){
  const me=STAFF[staff]?staff:"reece";
  const view=ownerView||"all";
  const [db,setDb]=useState({loading:true,ready:null,rows:[]});
  const [minDrop,setMinDrop]=useState(0.125);
  const [kindFilter,setKindFilter]=useState("all"); // all | pitch | quote

  const swapNow=useMemo(function(){
    var v=parseFloat(((rateSheets||{}).foaHecmAdj||{}).swapRate);
    return isNaN(v)?null:v;
  },[rateSheets]);

  // Two sources, same shape. The `scenarios` table is uncapped and reaches
  // leads that never became files — but it only holds runs logged since it was
  // created. Every file still carries its own priced history (capped per file),
  // and for anything priced before the migration that is the ONLY copy. So the
  // radar always reads both, keeps each person's newest run across the two,
  // and only then applies the threshold. When the table isn't there at all, the
  // file history alone carries the radar — useful on day one, not after a SQL
  // script.
  //
  // The table read takes no swap or threshold (those apply after the merge), so
  // changing the threshold never re-reads it. A pitch or disposition changes
  // `loans`, which re-reads it quietly (rows stay up) so a run just logged
  // under a lead is in the sweep. A stale response never overwrites a newer one.
  const reqRef=useRef(0);
  const fetchDb=useCallback(function(quiet){
    var my=++reqRef.current;
    if(!quiet)setDb(function(s){return Object.assign({},s,{loading:true});});
    scenariosReady().then(function(ok){
      return ok?fetchScenarioPeople():{rows:[],ready:false};
    }).then(function(r){
      if(my===reqRef.current)setDb({loading:false,ready:r.ready,rows:r.rows||[]});
    },function(){
      // The read failed outright: the file history still stands on its own.
      if(my===reqRef.current)setDb({loading:false,ready:true,rows:[]});
    });
  },[]);
  useEffect(function(){fetchDb(false);},[fetchDb]);
  const loansSeen=useRef(loans);
  useEffect(function(){
    if(loansSeen.current===loans)return;
    loansSeen.current=loans;
    var t=setTimeout(function(){fetchDb(true);},1500);
    return function(){clearTimeout(t);};
  },[loans,fetchDb]);
  const tableMissing=db.ready===false;

  // Every file's newest priced run (no threshold). A file whose lead link is
  // recorded only on the lead (converted_loan_id) still gets that lead's id, so
  // it lines up with the lead's runs in the table.
  const leadForLoan=useMemo(function(){
    var m=new Map();
    (mktgLeads||[]).forEach(function(x){if(x&&x.converted_loan_id&&!m.has(x.converted_loan_id))m.set(x.converted_loan_id,x.id);});
    return m;
  },[mktgLeads]);
  const fileHistory=useMemo(function(){
    if(swapNow==null)return[];
    return repriceCandidatesFromLoans(loans,swapNow,-Infinity).map(function(r){
      return r.lead_id||!leadForLoan.has(r.loan_id)?r:Object.assign({},r,{lead_id:leadForLoan.get(r.loan_id)});
    });
  },[loans,swapNow,leadForLoan]);

  // `people`: each person's newest run, any swap (drives the empty-state
  // explanation). `candidates`: the same, past the threshold.
  const people=useMemo(function(){
    if(swapNow==null||db.loading)return[];
    return tableMissing?fileHistory:mergeRepriceCandidates(db.rows,fileHistory,swapNow,-Infinity,Infinity);
  },[db,tableMissing,fileHistory,swapNow]);
  const candidates=useMemo(function(){
    if(swapNow==null||db.loading)return[];
    if(tableMissing)return fileHistory.filter(function(r){return r.swap_rate>=swapNow+minDrop-1e-9;});
    return mergeRepriceCandidates(db.rows,fileHistory,swapNow,minDrop,500);
  },[db,tableMissing,fileHistory,swapNow,minDrop]);

  // Whose row is it: the file's owner; for a lead-only scenario the lead's
  // owner tag, else the file it became; else Reece (everything before the
  // second login was his — same default as ownerOf).
  const loanById=useMemo(function(){return new Map((loans||[]).map(function(l){return [l.id,l];}));},[loans]);
  const leadById=useMemo(function(){return new Map((mktgLeads||[]).map(function(m){return [m.id,m];}));},[mktgLeads]);
  const rowOwner=useCallback(function(s){
    var loan=s.loan_id&&loanById.get(s.loan_id);
    if(loan)return ownerOf(loan);
    var lead=s.lead_id&&leadById.get(s.lead_id);
    if(lead){var o=leadOwner(lead);if(o)return o;var f=convertedLoanFor(lead,loans);if(f)return ownerOf(f);}
    return"reece";
  },[loanById,leadById,loans]);
  const inView=function(owner){return view==="all"?true:view==="mine"?owner===me:owner!==me;};

  // Pipeline order: estimate → kind filter → owner filter. Each stage is kept
  // so an empty result can say WHICH control emptied it.
  const withGain=useMemo(function(){
    return candidates.map(function(s){return Object.assign({},s,{gain:estimateGain(s,swapNow),owner:rowOwner(s)});});
  },[candidates,swapNow,rowOwner]);
  const byKind=useMemo(function(){
    return kindFilter==="all"?withGain:withGain.filter(function(s){return s.kind===kindFilter;});
  },[withGain,kindFilter]);
  const rows=useMemo(function(){
    var out=byKind.filter(function(s){return inView(s.owner);});
    out.sort(function(a,b){return b.gain-a.gain;});
    return out;
  },[byKind,view,me]);// eslint-disable-line react-hooks/exhaustive-deps
  // Per-owner counts for the toggle (after the kind filter, before owner).
  useEffect(function(){
    if(!onOwnerCounts)return;
    if(db.loading){onOwnerCounts(null);return;}
    var c={mine:0,other:0,all:byKind.length};
    byKind.forEach(function(s){if(s.owner===me)c.mine++;else c.other++;});
    onOwnerCounts(function(prev){return prev&&prev.mine===c.mine&&prev.other===c.other&&prev.all===c.all?prev:c;});
  },[byKind,me,db.loading,onOwnerCounts]);
  useEffect(function(){return function(){if(onOwnerCounts)onOwnerCounts(null);};},[onOwnerCounts]);

  // For the empty state: the swap each person was LAST priced at (newest run,
  // any swap), across the table and the history on the files.
  const lastPriced=useMemo(function(){
    if(swapNow==null)return null;
    if(!people.length)return{count:0,maxSwap:null};
    var mx=people.reduce(function(a,r){return Math.max(a,Number(r.swap_rate)||0);},0);
    return{count:people.length,maxSwap:mx};
  },[people,swapNow]);

  // Reopen the call on whichever record the scenario came from. Fall back to
  // the other id if the primary one has since been deleted.
  const openCall=function(s){
    if(!onStartCall)return;
    var loan=s.loan_id&&(loans||[]).find(function(l){return l.id===s.loan_id;});
    if(loan){onStartCall({prospect:prospectFromLoan(loan)});return;}
    var lead=s.lead_id&&(mktgLeads||[]).find(function(m){return m.id===s.lead_id;});
    if(lead){onStartCall({prospect:prospectFromLead(lead)});return;}
    // Record is gone but the scenario remembers the phone number — search on it.
    onStartCall({phone:s.phone||s.borrower_name||""});
  };

  if(swapNow==null){
    return(<div style={{padding:"28px 20px",border:"1px dashed "+C.border,borderRadius:12,color:C.textDim,fontSize:13,maxWidth:700}}>
      No 10-year swap rate set, so there's nothing to compare against.
      Enter today's swap in the calculator under <strong style={{color:C.text}}>Today's Market</strong> and the radar starts working.
    </div>);
  }

  // ── Nothing to show ──
  // The controls stay on screen whatever the result — a threshold or kind
  // filter that empties the list must be one click from undoing. The message
  // says WHICH control emptied it, and when nothing qualifies at all, whether
  // that's because the market went UP (then nobody's numbers improved).
  const empty=!db.loading&&rows.length===0;
  const fromFiles=tableMissing?0:rows.filter(function(s){return s._local;}).length;
  const otherName=STAFF[me==="max"?"reece":"max"].name;
  const kindLabel=kindFilter==="pitch"?"Pitched only":"Quoted, never pitched";
  const emptyWhy=(function(){
    if(!empty)return null;
    if(byKind.length>0){
      return{title:"Nobody here for "+(view==="mine"?"you":otherName),
        body:byKind.length+" "+(byKind.length===1?"person is":"people are")+" on the radar, all "+(view==="mine"?otherName+"'s":"yours")+". Switch Whose files to All (top right) to see them."};
    }
    if(withGain.length>0){
      return{title:"No one matches “"+kindLabel+"”",
        body:withGain.length+" "+(withGain.length===1?"person qualifies":"people qualify")+" at this threshold under Everything."};
    }
    var lp=lastPriced;
    if(!lp||!lp.count||lp.maxSwap==null){
      return{title:"Nobody to call back yet",
        body:"No file has a priced run with a recorded swap yet. Every call you run from here on adds to this."};
    }
    if(lp.maxSwap<=swapNow+1e-9){
      var up=swapNow-lp.maxSwap;
      return{title:"Rates are up — nobody's numbers improved",
        body:"Today's swap ("+swapNow.toFixed(3)+"%) is "+(up>0.0005?up.toFixed(3)+"% above":"level with")+" the highest swap any file was last priced at ("+lp.maxSwap.toFixed(3)+"%). Everyone would get the same or less today, so there's no one to call with better news yet."};
    }
    var best=lp.maxSwap-swapNow;
    if(best<minDrop-1e-9){
      return{title:"Nobody at "+minDrop.toFixed(3)+"% or more",
        body:"The biggest drop on file is "+best.toFixed(3)+"% (last priced at "+lp.maxSwap.toFixed(3)+"%). Pick a smaller threshold to see them."};
    }
    return{title:"Nobody to call back yet",
      body:"Nothing was priced at a swap "+minDrop.toFixed(3)+"% above today's "+swapNow.toFixed(3)+"%. Every call you run from here on adds to this."};
  })();

  return(<div>
    {/* ── Controls ── */}
    <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap",marginBottom:16}}>
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:10,padding:"10px 16px"}}>
        <div style={{fontSize:9.5,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6}}>10-yr swap today</div>
        <div style={{fontSize:19,fontWeight:800,color:C.text}}>{swapNow.toFixed(3)}%</div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:C.textDim,fontWeight:700}}>PRICED AT LEAST</span>
        {[0.125,0.25,0.5,1.0].map(function(d){
          var on=minDrop===d;
          return <button key={d} onClick={function(){setMinDrop(d);}} style={{padding:"6px 12px",borderRadius:20,fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",
            border:"1px solid "+(on?C.accent:C.border),background:on?C.accentGlow:"transparent",color:on?C.accentLight:C.textMuted}}>{d.toFixed(3)}% higher</button>;
        })}
      </div>
      <div style={{display:"flex",alignItems:"center",gap:7}}>
        {[{v:"all",l:"Everything"},{v:"pitch",l:"Pitched only"},{v:"quote",l:"Quoted, never pitched"}].map(function(o){
          var on=kindFilter===o.v;
          return <button key={o.v} onClick={function(){setKindFilter(o.v);}} style={{padding:"6px 12px",borderRadius:20,fontSize:11.5,fontWeight:700,cursor:"pointer",fontFamily:"'Mulish',system-ui,sans-serif",
            border:"1px solid "+(on?C.gold:C.border),background:on?C.goldBg:"transparent",color:on?C.gold:C.textMuted}}>{o.l}</button>;
        })}
      </div>
      <Btn small onClick={function(){fetchDb(false);}} style={{color:C.accentLight,marginLeft:"auto"}}>↻ Refresh</Btn>
    </div>

    <div style={{fontSize:11.5,color:C.textDim,marginBottom:16,lineHeight:1.6,maxWidth:840}}>
      Everyone you priced when the 10-year swap was higher than it is now. A lower expected rate
      means a higher principal limit on the same house — they qualify for more today than they did
      when you last spoke, and they don't know it.
      <strong style={{color:C.textMuted}}> The dollar figure is a rough estimate for ranking the list only</strong> —
      open the call and re-run the calculator for the real number.
    </div>

    {db.loading?
      <div style={{padding:"28px 20px",textAlign:"center",color:C.textDim,fontSize:13}}>Sweeping scenario history…</div>
    :empty?
      <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:14,padding:"22px 24px",maxWidth:820}}>
        <h3 style={{margin:"0 0 8px",fontSize:15,fontWeight:800,color:C.textMuted}}>{emptyWhy.title}</h3>
        <p style={{margin:0,fontSize:13,color:C.textMuted,lineHeight:1.6}}>{emptyWhy.body}</p>
        {tableMissing&&<div style={{marginTop:14,paddingTop:12,borderTop:"1px solid "+C.border}}>
          <p style={{margin:"0 0 8px",fontSize:12.5,color:C.textMuted,lineHeight:1.6}}>
            <strong style={{color:C.orange}}>Running on local history.</strong> The radar is reading the priced history stored
            on each loan file, which is capped at the last {12} runs per file. For permanent, uncapped history — and to sweep
            leads that never became files — run
            <code style={{background:C.surfaceAlt,padding:"2px 7px",borderRadius:5,margin:"0 5px",fontSize:12}}>supabase/2026-08-20-scenarios-and-contacts.sql</code>
            once in <strong style={{color:C.text}}>Supabase → SQL Editor</strong>.
          </p>
          <p style={{margin:0,fontSize:12,color:C.textDim,lineHeight:1.6}}>
            Nothing is lost either way — this only changes how far back the sweep can reach.
          </p>
        </div>}
      </div>
    :<div style={{display:"flex",flexDirection:"column",gap:8}}>
      <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:2,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
        <span>{rows.length} {rows.length===1?"person is":"people are"} worth a call back</span>
        {tableMissing&&<Badge label="from local file history" color={C.orange} bg={C.orangeBg}/>}
        {fromFiles>0&&<span style={{fontSize:11,fontWeight:600,color:C.textDim}}>includes history saved on files</span>}
      </div>
      {rows.map(function(s){
        return(<div key={(s._local?"file:":"")+s.id} style={{background:C.card,border:"1px solid "+C.border,borderRadius:12,padding:"14px 16px",display:"flex",gap:14,alignItems:"flex-start",flexWrap:"wrap"}}>
          <div style={{flex:"1 1 240px",minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              {view==="all"&&<OwnerChip owner={s.owner}/>}
              <span style={{fontSize:14,fontWeight:800,color:C.text}}>{s.borrower_name||"(no name on the record)"}</span>
              <Badge label={s.kind==="pitch"?"was pitched":"quoted only"}
                color={s.kind==="pitch"?C.green:C.cyan} bg={(s.kind==="pitch"?C.green:C.cyan)+"18"}/>
              {!s.loan_id&&!s.lead_id&&<Badge label="record deleted" color={C.textDim} bg={C.textDim+"18"}/>}
            </div>
            <div style={{fontSize:11.5,color:C.textDim,marginTop:3}}>{s.property||"no address on the record"}</div>
            <div style={{fontSize:11.5,color:C.textMuted,marginTop:6,lineHeight:1.65}}>
              Priced {fmtWhen(s.run_at)} at a {Number(s.swap_rate).toFixed(3)}% swap
              <span style={{color:C.green,fontWeight:700}}> · {s.swapDrop.toFixed(3)}% lower today</span>
              <br/>
              {s.product||"—"}{s.lender?" · "+s.lender:""} — they were looking at {$(s.cash_to_borrower)} then
            </div>
          </div>

          <div style={{flex:"0 0 auto",textAlign:"right",minWidth:150}}>
            <div style={{fontSize:9.5,fontWeight:800,color:C.textDim,textTransform:"uppercase",letterSpacing:.6}}>Rough upside</div>
            <div style={{fontSize:21,fontWeight:800,color:s.gain>0?C.green:C.textDim}}>{s.gain>0?"+"+$(s.gain):"—"}</div>
            <div style={{fontSize:10,color:C.textDim}}>estimate · re-run to confirm</div>
          </div>

          <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap"}}>
            {s.phone&&<CallBtn phone={s.phone} small/>}
            {onStartCall&&<Btn small primary onClick={function(){openCall(s);}}>{Ic.phone} Re-price it</Btn>}
          </div>
        </div>);
      })}
    </div>}
  </div>);
}

export default RepriceRadar;
